import transformKeyCase from 'Utils/transformKeyCase';
import { type AnyObject } from '@Types';
import { type StreamProgressRecord, type LessonProgressRecord, type ProjectProgressRecord } from 'FrontRoyalStore';
import { angularInjectorProvider } from 'Injector';
import { type ErrorLogService as ErrorLogServiceClass } from 'ErrorLogging';
import {
    type LessonProgressIguanaObject,
    type StreamProgressIguanaObject,
    type LessonProgress,
    type ProjectProgress,
    type StreamProgress,
    type GetAllProgressResponse,
    type useContentProgressResponse,
} from '../ContentProgress.types';

export function convertGetAllProgressResponse(
    iguanaFormattedResponse: GetAllProgressResponse,
): useContentProgressResponse {
    const streamProgress = iguanaFormattedResponse.streamProgress.map(r =>
        transformKeyCase<StreamProgress>(r.asJson(), { to: 'camelCase' }),
    );
    const lessonProgress = iguanaFormattedResponse.lessonProgress.map(r =>
        transformKeyCase<LessonProgress>(r.asJson(), { to: 'camelCase' }),
    );
    const projectProgress = iguanaFormattedResponse.projectProgress.map(r =>
        transformKeyCase<ProjectProgress>(r, { to: 'camelCase' }),
    );
    const streamProgressByLocalePackId = streamProgress.reduce<AnyObject<StreamProgress>>((acc, curr) => {
        acc[curr.localePackId] = curr;
        return acc;
    }, {});
    const lessonProgressByLocalePackId = lessonProgress.reduce<AnyObject<LessonProgress>>((acc, curr) => {
        acc[curr.localePackId] = curr;
        return acc;
    }, {});
    const projectProgressByRequirementIdentifier = projectProgress.reduce<AnyObject<ProjectProgress>>((acc, curr) => {
        acc[curr.requirementIdentifier] = curr;
        return acc;
    }, {});

    const iguanaStreamProgressByLocalePackId = iguanaFormattedResponse.streamProgress.reduce<
        AnyObject<StreamProgressIguanaObject>
    >((acc, curr) => {
        acc[curr.locale_pack_id] = curr;
        return acc;
    }, {});

    const iguanaLessonProgressByLocalePackId = iguanaFormattedResponse.lessonProgress.reduce<
        AnyObject<LessonProgressIguanaObject>
    >((acc, curr) => {
        acc[curr.locale_pack_id] = curr;
        return acc;
    }, {});

    return {
        streamProgress,
        lessonProgress,
        projectProgress,
        streamProgressByLocalePackId,
        lessonProgressByLocalePackId,
        projectProgressByRequirementIdentifier,
        // FIXME: See comment in ContentProgress.types.ts#useContentProgressResponse about why we're adding these
        iguanaStreamProgressByLocalePackId,
        iguanaLessonProgressByLocalePackId,
    };
}

type RawProgressResponse = {
    streamProgress: StreamProgressRecord[];
    lessonProgress: LessonProgressRecord[];
    projectProgress: ProjectProgressRecord[];
    favoriteStreamsSet: Record<string, boolean>;
};

// FIXME: See comment in ContentProgress.types.ts#useContentProgressResponse about why we need these.
// We create real Iguana objects here because the Angular studentDashboardPlaylistsBox directive
// assigns them onto cached Iguana stream/lesson objects. Once the curriculum tab is migrated
// to React, these fields and this Iguana dependency can be removed.
function buildIguanaProgressMaps(rawResponse: RawProgressResponse) {
    const StreamProgressFactory = angularInjectorProvider.safeGet<{
        new: (attrs: AnyObject) => StreamProgressIguanaObject;
    }>('Lesson.StreamProgress');
    const LessonProgressFactory = angularInjectorProvider.safeGet<{
        new: (attrs: AnyObject) => LessonProgressIguanaObject;
    }>('LessonProgress');

    // The iguana factories should always be registered once the Angular app is bootstrapped.
    // A null factory here means the Angular studentDashboardPlaylistsBox directive will silently
    // render without stream/lesson progress, which is invisible to users.
    if (!StreamProgressFactory || !LessonProgressFactory) {
        const ErrorLogService = angularInjectorProvider.safeGet<typeof ErrorLogServiceClass>('ErrorLogService');
        ErrorLogService?.notifyInProd('Iguana progress factories missing in buildIguanaProgressMaps', null, {
            level: 'warning',
            has_stream_progress_factory: !!StreamProgressFactory,
            has_lesson_progress_factory: !!LessonProgressFactory,
        });
    }

    const iguanaStreamProgressByLocalePackId = rawResponse.streamProgress.reduce<AnyObject<StreamProgressIguanaObject>>(
        (acc, curr) => {
            if (StreamProgressFactory) acc[curr.locale_pack_id] = StreamProgressFactory.new(curr);
            return acc;
        },
        {},
    );

    const iguanaLessonProgressByLocalePackId = rawResponse.lessonProgress.reduce<AnyObject<LessonProgressIguanaObject>>(
        (acc, curr) => {
            if (LessonProgressFactory) acc[curr.locale_pack_id] = LessonProgressFactory.new(curr);
            return acc;
        },
        {},
    );

    return { iguanaStreamProgressByLocalePackId, iguanaLessonProgressByLocalePackId };
}

// This function takes raw snake_case records (from IndexedDB / native fetch) and produces
// the same useContentProgressResponse shape as convertGetAllProgressResponse, but without
// requiring Iguana-wrapped input.
export function convertRawProgressToContentProgressResponse(
    rawResponse: RawProgressResponse,
): useContentProgressResponse {
    const streamProgress = rawResponse.streamProgress.map(r =>
        transformKeyCase<StreamProgress>(r, { to: 'camelCase' }),
    );
    const lessonProgress = rawResponse.lessonProgress.map(r =>
        transformKeyCase<LessonProgress>(r, { to: 'camelCase' }),
    );
    const projectProgress = rawResponse.projectProgress.map(r =>
        transformKeyCase<ProjectProgress>(r, { to: 'camelCase' }),
    );

    const streamProgressByLocalePackId = streamProgress.reduce<AnyObject<StreamProgress>>((acc, curr) => {
        acc[curr.localePackId] = curr;
        return acc;
    }, {});
    const lessonProgressByLocalePackId = lessonProgress.reduce<AnyObject<LessonProgress>>((acc, curr) => {
        acc[curr.localePackId] = curr;
        return acc;
    }, {});
    const projectProgressByRequirementIdentifier = projectProgress.reduce<AnyObject<ProjectProgress>>((acc, curr) => {
        acc[curr.requirementIdentifier] = curr;
        return acc;
    }, {});

    const { iguanaStreamProgressByLocalePackId, iguanaLessonProgressByLocalePackId } =
        buildIguanaProgressMaps(rawResponse);

    return {
        streamProgress,
        lessonProgress,
        projectProgress,
        streamProgressByLocalePackId,
        lessonProgressByLocalePackId,
        projectProgressByRequirementIdentifier,
        iguanaStreamProgressByLocalePackId,
        iguanaLessonProgressByLocalePackId,
    };
}
